import type { TTSOptions, TTSProvider } from './types';

export class BrowserTTSProvider implements TTSProvider {
  name = 'browser';
  private synth = window.speechSynthesis;
  private utt: SpeechSynthesisUtterance | null = null;
  private settings: TTSOptions;

  constructor(defaults: TTSOptions = {}) {
    this.settings = { ...defaults };
  }

  isSupported = (): boolean => typeof window !== 'undefined' && 'speechSynthesis' in window;

  setVolume = (v: number): void => {
    if (this.utt) this.utt.volume = v;
    this.settings.volume = v;
  };

  setRate = (r: number): void => {
    if (this.utt) this.utt.rate = r;
    this.settings.rate = r;
  };

  setLang = (lang: string): void => {
    this.settings.lang = lang;
  };

  setVoice = (voice: string): void => {
    this.settings.voice = voice;
  };

  setOnEnded = (cb: () => void): void => {
    if (this.utt) this.utt.onend = cb;
  };

  clearCache = (): void => void 0;

  isPlaying = (t?: string): boolean => {
    const speaking = this.synth.speaking;
    return speaking && (!t || this.utt?.text === t);
  };

  play = async (t: string, o?: TTSOptions): Promise<void> => {
    this.stop();
    const { lang, voice, rate, pitch, volume } = { ...this.settings, ...(o || {}) };
    const u = new SpeechSynthesisUtterance(t);
    if (lang) u.lang = lang;
    if (voice) {
      const found = this.synth.getVoices().find((v) => v.name === voice);
      if (found) u.voice = found;
    }
    if (rate) u.rate = rate;
    if (pitch) u.pitch = pitch;
    if (volume) u.volume = volume;

    this.utt = u;
    this.synth.speak(u);
  };

  pause = (): void => {
    this.synth.pause();
  };

  resume = (): Promise<void> => {
    return Promise.resolve(this.synth.resume());
  };

  stop = (): void => {
    this.synth.cancel();
    this.utt = null;
  };

  reset = (): void => {
    this.synth.cancel();
    this.utt = null;
  };

  abortRequest = (): void => void 0;

  getSettings = (): TTSOptions => {
    return { ...this.settings };
  };
}
